import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, CircleSlash, History, Landmark, Loader2, PauseCircle, PlayCircle, ScrollText, X, IndianRupee } from "lucide-react";
import { api, fmt, fmtDate, apiError } from "@/lib/api";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "pending", label: "Pending Review" },
  { id: "approved", label: "Approved" },
  { id: "suspended", label: "Suspended" },
  { id: "overdue", label: "Overdue" },
];

const STATUS_CHIP = {
  APPROVED: "border-emerald-500/40 text-emerald-400",
  SUSPENDED: "border-red-500/40 text-red-400",
  PENDING_REVIEW: "border-amber-500/40 text-amber-400",
  MORE_INFO: "border-sky-500/40 text-sky-400",
  REJECTED: "border-white/20 text-neutral-500",
};

const inputCls = "h-11 w-full rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

function Modal({ title, onClose, children, testid }) {
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
        className="fixed inset-x-4 top-[10vh] z-[85] mx-auto max-w-md rounded-2xl border border-white/10 bg-[#F1EDE3] p-6" data-testid={testid}>
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </motion.div>
    </>
  );
}

export default function AdminCredit() {
  const [data, setData] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null); // {type, row}
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [statementFor, setStatementFor] = useState(null);
  const [statement, setStatement] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/credit/accounts?filter=${filter}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e, "Couldn't load credit accounts")));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (showAudit && !auditLog) api.get("/admin/credit/audit").then(({ data }) => setAuditLog(data)).catch(() => {});
  }, [showAudit, auditLog]);

  const openStatement = async (row) => {
    setStatementFor(row);
    setStatement(null);
    try {
      const { data } = await api.get(`/admin/credit/accounts/${row.id}/statement`);
      setStatement(data);
    } catch (e) { toast.error(apiError(e, "Couldn't load statement")); }
  };

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setModal(null);
      setForm({});
      setAuditLog(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Action failed"));
    } finally {
      setBusy(false);
    }
  };

  const chip = "rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider";
  const iconBtn = "flex h-8 items-center gap-1.5 rounded-lg border border-white/10 px-2.5 text-[11px] font-bold text-neutral-400 transition-colors hover:text-white disabled:opacity-40";

  return (
    <div data-testid="admin-credit">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2 overflow-x-auto" data-testid="credit-filters">
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`credit-filter-${f.id}`} onClick={() => setFilter(f.id)}
              className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-xs font-bold transition-colors ${
                filter === f.id ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-400 hover:text-white"
              }`}>
              {f.label}
              {data && <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${filter === f.id ? "bg-amber-500 text-black" : "bg-white/10"}`}>{data.counts[f.id]}</span>}
            </button>
          ))}
        </div>
        <button data-testid="credit-audit-toggle" onClick={() => setShowAudit((v) => !v)}
          className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-xs font-bold transition-colors ${showAudit ? "border-amber-500/60 text-amber-400" : "border-white/10 text-neutral-400 hover:text-white"}`}>
          <History className="h-4 w-4" /> Audit Log
        </button>
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      ) : (
        <>
          {(filter === "all" || filter === "pending") && data.applications.length > 0 && (
            <section className="mb-6" data-testid="credit-applications">
              <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">
                <ScrollText className="h-4 w-4" /> Credit Applications
              </h3>
              <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                      <th className="px-5 py-4">Business</th><th className="px-5 py-4">Customer</th><th className="px-5 py-4">GSTIN</th>
                      <th className="px-5 py-4">Requested Limit</th><th className="px-5 py-4">Term</th><th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.applications.map((a) => (
                      <tr key={a.id} className="border-b border-white/[0.04]" data-testid={`application-row-${a.id}`}>
                        <td className="px-5 py-3.5 font-semibold text-white">{a.business_name}</td>
                        <td className="px-5 py-3.5"><p className="text-neutral-300">{a.contact_name}</p><p className="text-xs text-neutral-600">{a.email}</p></td>
                        <td className="px-5 py-3.5 font-mono text-xs text-neutral-400">{a.gstin || "—"}</td>
                        <td className="px-5 py-3.5 text-neutral-300">{fmt(a.requested_limit)}</td>
                        <td className="px-5 py-3.5 text-neutral-300">{a.requested_term_days} days</td>
                        <td className="px-5 py-3.5"><span className={`${chip} ${STATUS_CHIP[a.status]}`}>{a.status.replace("_", " ")}</span></td>
                        <td className="px-5 py-3.5">
                          <div className="flex justify-end gap-2">
                            <button data-testid={`approve-${a.id}`} onClick={() => { setModal({ type: "approve", row: a }); setForm({ credit_limit: a.requested_limit, payment_term_days: a.requested_term_days, reason: "" }); }} className={`${iconBtn} border-emerald-500/30 text-emerald-400 hover:border-emerald-500/60`}>
                              <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                            </button>
                            <button data-testid={`request-info-${a.id}`} onClick={() => { setModal({ type: "info", row: a }); setForm({ reason: "" }); }} className={iconBtn}>
                              Request Info
                            </button>
                            <button data-testid={`reject-${a.id}`} onClick={() => { setModal({ type: "reject", row: a }); setForm({ reason: "" }); }} className={`${iconBtn} hover:border-red-500/50 hover:text-red-400`}>
                              <CircleSlash className="h-3.5 w-3.5" /> Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <section data-testid="credit-accounts">
            <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">
              <Landmark className="h-4 w-4" /> Credit Accounts
            </h3>
            {data.accounts.length === 0 ? (
              <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] py-14 text-center text-sm text-neutral-500" data-testid="credit-accounts-empty">
                No credit accounts in this view.
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
                <table className="w-full min-w-[1050px] text-left text-sm" data-testid="credit-accounts-table">
                  <thead>
                    <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                      <th className="px-5 py-4">Customer</th><th className="px-5 py-4">Business</th><th className="px-5 py-4">Credit Limit</th>
                      <th className="px-5 py-4">Available</th><th className="px-5 py-4">Outstanding</th><th className="px-5 py-4">Overdue</th>
                      <th className="px-5 py-4">Status</th><th className="px-5 py-4">Next Due</th><th className="px-5 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.accounts.map((r) => (
                      <tr key={r.id} className="border-b border-white/[0.04]" data-testid={`credit-account-row-${r.id}`}>
                        <td className="px-5 py-3.5"><p className="font-semibold text-white">{r.customer_name}</p><p className="text-xs text-neutral-600">{r.customer_email}</p></td>
                        <td className="px-5 py-3.5 text-neutral-300">{r.business_name}</td>
                        <td className="px-5 py-3.5 text-neutral-300">{fmt(r.credit_limit)}<span className="block text-[10px] text-neutral-600">{r.payment_term_days}-day terms</span></td>
                        <td className="px-5 py-3.5 font-bold text-emerald-400">{fmt(r.available_credit)}</td>
                        <td className={`px-5 py-3.5 font-bold ${r.outstanding > 0 ? "text-amber-400" : "text-neutral-500"}`}>{fmt(r.outstanding)}</td>
                        <td className={`px-5 py-3.5 font-bold ${r.overdue > 0 ? "text-red-400" : "text-neutral-500"}`}>{fmt(r.overdue)}</td>
                        <td className="px-5 py-3.5">
                          <span className={`${chip} ${STATUS_CHIP[r.credit_status]}`} data-testid={`credit-account-status-${r.id}`}>{r.credit_status}</span>
                          {r.overdue > 0 && <span className={`${chip} mt-1 block w-fit border-red-500/40 text-red-400`}>OVERDUE</span>}
                        </td>
                        <td className="px-5 py-3.5 text-neutral-400">{r.next_due_date ? fmtDate(r.next_due_date) : "—"}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <button data-testid={`statement-${r.id}`} onClick={() => openStatement(r)} className={iconBtn}>Statement</button>
                            <button data-testid={`record-payment-${r.id}`} onClick={() => { setModal({ type: "payment", row: r }); setForm({ amount: r.outstanding, note: "" }); }} disabled={r.outstanding <= 0} className={`${iconBtn} border-emerald-500/30 text-emerald-400`}>
                              <IndianRupee className="h-3.5 w-3.5" /> Payment
                            </button>
                            <button data-testid={`limit-${r.id}`} onClick={() => { setModal({ type: "limit", row: r }); setForm({ credit_limit: r.credit_limit, reason: "" }); }} className={iconBtn}>Limit</button>
                            <button data-testid={`adjust-${r.id}`} onClick={() => { setModal({ type: "adjust", row: r }); setForm({ amount: "", reason: "" }); }} className={iconBtn}>Adjust</button>
                            {r.credit_status === "SUSPENDED" ? (
                              <button data-testid={`reactivate-${r.id}`} onClick={() => run(() => api.post(`/admin/credit/accounts/${r.id}/reactivate`, { reason: "Reactivated by admin" }), "Account reactivated")} className={`${iconBtn} border-emerald-500/30 text-emerald-400`}>
                                <PlayCircle className="h-3.5 w-3.5" /> Reactivate
                              </button>
                            ) : (
                              <button data-testid={`suspend-${r.id}`} onClick={() => { setModal({ type: "suspend", row: r }); setForm({ reason: "" }); }} className={`${iconBtn} hover:border-red-500/50 hover:text-red-400`}>
                                <PauseCircle className="h-3.5 w-3.5" /> Suspend
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {showAudit && (
            <section className="mt-6" data-testid="credit-audit-log">
              <h3 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">
                <History className="h-4 w-4" /> Audit Log
              </h3>
              <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
                {!auditLog ? (
                  <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
                ) : auditLog.length === 0 ? (
                  <p className="py-10 text-center text-sm text-neutral-500">No admin credit actions recorded yet.</p>
                ) : (
                  <table className="w-full min-w-[760px] text-left text-xs">
                    <thead>
                      <tr className="border-b border-white/[0.07] text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                        <th className="px-5 py-3">When</th><th className="px-5 py-3">Admin</th><th className="px-5 py-3">Action</th>
                        <th className="px-5 py-3">Old → New</th><th className="px-5 py-3">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map((a) => (
                        <tr key={a.id} className="border-b border-white/[0.04]" data-testid={`audit-row-${a.id}`}>
                          <td className="px-5 py-2.5 text-neutral-500">{fmtDate(a.timestamp)}</td>
                          <td className="px-5 py-2.5 text-neutral-400">{a.admin}</td>
                          <td className="px-5 py-2.5 font-bold text-white">{a.action.replaceAll("_", " ")}</td>
                          <td className="px-5 py-2.5 font-mono text-neutral-400">{a.old_value != null ? JSON.stringify(a.old_value) : "—"} → {a.new_value != null ? JSON.stringify(a.new_value) : "—"}</td>
                          <td className="px-5 py-2.5 text-neutral-500">{a.reason || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          )}
        </>
      )}

      <AnimatePresence>
        {modal?.type === "approve" && (
          <Modal title={`Approve — ${modal.row.business_name}`} onClose={() => setModal(null)} testid="approve-modal">
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Credit limit ₹</label>
                <input data-testid="approve-limit" type="number" min="1" value={form.credit_limit} onChange={(e) => setForm((f) => ({ ...f, credit_limit: e.target.value }))} className={inputCls} />
              </div>
              <div>
                <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Payment term (max 30 days)</label>
                <select data-testid="approve-term" value={form.payment_term_days} onChange={(e) => setForm((f) => ({ ...f, payment_term_days: e.target.value }))} className={inputCls}>
                  {[7, 15, 30].map((t) => <option key={t} value={t}>{`${t} days`}</option>)}
                </select>
              </div>
              <input data-testid="approve-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Note (optional)" className={inputCls} />
              <button data-testid="approve-confirm" disabled={busy}
                onClick={() => run(() => api.post(`/admin/credit/applications/${modal.row.id}/approve`, { credit_limit: parseFloat(form.credit_limit) || 0, payment_term_days: parseInt(form.payment_term_days), reason: form.reason }), "Application approved — credit account activated")}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 text-sm font-bold text-black hover:bg-emerald-400 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Approve & Activate
              </button>
            </div>
          </Modal>
        )}

        {(modal?.type === "reject" || modal?.type === "info") && (
          <Modal title={modal.type === "reject" ? `Reject — ${modal.row.business_name}` : `Request Info — ${modal.row.business_name}`} onClose={() => setModal(null)} testid="reject-modal">
            <div className="space-y-4">
              <textarea data-testid="reject-reason" rows={3} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder={modal.type === "reject" ? "Reason for rejection" : "What information do you need?"} className={`${inputCls} h-auto py-3`} />
              <button data-testid="reject-confirm" disabled={busy}
                onClick={() => run(() => api.post(`/admin/credit/applications/${modal.row.id}/${modal.type === "reject" ? "reject" : "request-info"}`, { reason: form.reason }), modal.type === "reject" ? "Application rejected" : "Information requested")}
                className={`flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold disabled:opacity-60 ${modal.type === "reject" ? "bg-red-500/90 text-white hover:bg-red-500" : "bg-amber-500 text-black hover:bg-amber-400"}`}>
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Confirm
              </button>
            </div>
          </Modal>
        )}

        {modal?.type === "suspend" && (
          <Modal title={`Suspend — ${modal.row.customer_name}`} onClose={() => setModal(null)} testid="suspend-modal">
            <div className="space-y-4">
              <p className="text-xs leading-relaxed text-neutral-400">The customer won't be able to place new credit orders, but can still pay outstanding balances and use normal payment methods.</p>
              <textarea data-testid="suspend-reason" rows={3} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Reason for suspension" className={`${inputCls} h-auto py-3`} />
              <button data-testid="suspend-confirm" disabled={busy}
                onClick={() => run(() => api.post(`/admin/credit/accounts/${modal.row.id}/suspend`, { reason: form.reason }), "Account suspended")}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-500/90 py-3.5 text-sm font-bold text-white hover:bg-red-500 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Suspend Account
              </button>
            </div>
          </Modal>
        )}

        {modal?.type === "payment" && (
          <Modal title={`Record Payment — ${modal.row.customer_name}`} onClose={() => setModal(null)} testid="payment-modal">
            <div className="space-y-4">
              <p className="text-xs text-neutral-500">Outstanding: <span className="font-bold text-amber-400">{fmt(modal.row.outstanding)}</span></p>
              <input data-testid="payment-amount" type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="Amount received" className={inputCls} />
              <input data-testid="payment-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Note (e.g. UPI ref)" className={inputCls} />
              <button data-testid="payment-confirm" disabled={busy}
                onClick={() => run(() => api.post(`/admin/credit/accounts/${modal.row.id}/record-payment`, { amount: parseFloat(form.amount) || 0, note: form.note }), "Payment recorded")}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 py-3.5 text-sm font-bold text-black hover:bg-emerald-400 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Record Payment
              </button>
            </div>
          </Modal>
        )}

        {modal?.type === "limit" && (
          <Modal title={`Change Limit — ${modal.row.customer_name}`} onClose={() => setModal(null)} testid="limit-modal">
            <div className="space-y-4">
              <p className="text-xs text-neutral-500">Current: {fmt(modal.row.credit_limit)} · Outstanding: {fmt(modal.row.outstanding)}</p>
              <input data-testid="limit-value" type="number" min="1" value={form.credit_limit} onChange={(e) => setForm((f) => ({ ...f, credit_limit: e.target.value }))} className={inputCls} />
              <input data-testid="limit-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Reason" className={inputCls} />
              <button data-testid="limit-confirm" disabled={busy}
                onClick={() => run(() => api.post(`/admin/credit/accounts/${modal.row.id}/limit`, { credit_limit: parseFloat(form.credit_limit) || 0, reason: form.reason }), "Credit limit updated")}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-black hover:bg-amber-400 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update Limit
              </button>
            </div>
          </Modal>
        )}

        {modal?.type === "adjust" && (
          <Modal title={`Credit Adjustment — ${modal.row.customer_name}`} onClose={() => setModal(null)} testid="adjust-modal">
            <div className="space-y-4">
              <p className="text-xs text-neutral-500">Positive adds to the balance owed; negative reduces it (e.g. goodwill credit).</p>
              <input data-testid="adjust-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="Amount (e.g. -500)" className={inputCls} />
              <input data-testid="adjust-reason" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Reason (required)" className={inputCls} />
              <button data-testid="adjust-confirm" disabled={busy || !form.reason || !form.amount}
                onClick={() => run(() => api.post(`/admin/credit/accounts/${modal.row.id}/adjust`, { amount: parseFloat(form.amount) || 0, reason: form.reason }), "Adjustment recorded")}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-black hover:bg-amber-400 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Record Adjustment
              </button>
            </div>
          </Modal>
        )}

        {statementFor && (
          <Modal title={`Statement — ${statementFor.customer_name}`} onClose={() => { setStatementFor(null); setStatement(null); }} testid="statement-modal">
            {!statement ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
            ) : statement.length === 0 ? (
              <p className="py-6 text-center text-sm text-neutral-500">No transactions yet.</p>
            ) : (
              <div className="max-h-[50vh] space-y-2 overflow-y-auto" data-lenis-prevent>
                {statement.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/[0.06] bg-black/30 px-4 py-3 text-xs" data-testid={`admin-statement-${tx.id}`}>
                    <div>
                      <p className="font-bold text-white">{tx.kind === "debit" ? `Order ${tx.order_number || ""}` : tx.kind === "payment" ? "Payment received" : "Adjustment"}</p>
                      <p className="mt-0.5 text-neutral-500">{fmtDate(tx.created_at)}{tx.due_date ? ` · due ${fmtDate(tx.due_date)}` : ""}{tx.note ? ` · ${tx.note}` : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${tx.kind === "payment" || tx.amount < 0 ? "text-emerald-400" : "text-white"}`}>
                        {tx.kind === "payment" || tx.amount < 0 ? "−" : "+"}{fmt(Math.abs(tx.amount))}
                      </p>
                      <p className="text-[10px] uppercase text-neutral-500">{tx.status}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
}
